import { useRecoilState, useRecoilValue } from "recoil";
import { artistListState, totalThumbnailUrlListState } from "../atom/atom";
import {
  fetchAndSetData,
  fetchArtistList,
  fetchFilesFromStorage,
} from "../api/fetch.api";
import { localStorageUtil } from "../module/util";
import { LOCAL_STORAGE_THUMBNAIL_KEY } from "../module/constant";
import { uploadFileToStorage } from "../api/storage/storage.api";

export const useThumbnail = () => {
  const [totalArtistThumbnailList, setTotalArtistThumbnailList] =
    useRecoilState<{
      [key: string]: string[];
    }>(totalThumbnailUrlListState);

  const [totalArtistList, setTotalArtistList] = useRecoilState(artistListState);

  const localStorageKey = LOCAL_STORAGE_THUMBNAIL_KEY;

  const refetchArtistThumbnailList = async () => {
    try {
      // Clear existing cache
      localStorageUtil.remove(localStorageKey);

      // Fetch fresh artist list
      await fetchAndSetData(fetchArtistList, setTotalArtistList);

      // Fetch fresh thumbnails for all artists
      const artistThumbnails: { [key: string]: string[] } = {};

      for (const artist of totalArtistList) {
        const thumbnails = await fetchFilesFromStorage(
          `/artistThumbnail/${artist.artistName}/`
        );
        artistThumbnails[artist.artistName] = thumbnails;
      }

      // Update local storage and state
      localStorageUtil.set(localStorageKey, artistThumbnails);
      setTotalArtistThumbnailList(artistThumbnails);

      return artistThumbnails;
    } catch (error) {
      console.error("Error refetching artist thumbnails:", error);
      throw error;
    }
  };

  const fetchAllArtistThumbnailList = async () => {
    await fetchAndSetData(fetchArtistList, setTotalArtistList);

    const cachedThumbnails = localStorageUtil.get<{ [key: string]: string[] }>(
      localStorageKey
    );

    if (cachedThumbnails) {
      setTotalArtistThumbnailList(cachedThumbnails);
      return;
    }

    try {
      const artistThumbnails: { [key: string]: string[] } = {};

      for (const artist of totalArtistList) {
        const thumbnails = await fetchFilesFromStorage(
          `/artistThumbnail/${artist.artistName}/`
        );
        artistThumbnails[artist.artistName] = thumbnails;
      }

      localStorageUtil.set(localStorageKey, artistThumbnails);
      setTotalArtistThumbnailList(artistThumbnails);
    } catch (error) {
      console.error("Error fetching all artist thumbnails:", error);
    }
  };

  const removeCachedArtistThumbnailList = () => {
    localStorageUtil.remove(localStorageKey);
  };

  const uploadArtistThumbnail = async (artistName: string, file: File) => {
    try {
      const path = `/artistThumbnail/${artistName}/${file.name}`; // Firebase 경로에 파일 이름 포함
      const thumbnailUrl = await uploadFileToStorage(path, file); // 썸네일 업로드
      console.log("Thumbnail uploaded successfully:", thumbnailUrl);

      // 기존 썸네일 리스트에 새로 업로드한 썸네일 추가
      setTotalArtistThumbnailList((prev) => ({
        ...prev,
        [artistName]: [...(prev[artistName] || []), thumbnailUrl],
      }));

      // 로컬 스토리지에 업데이트된 썸네일 리스트 저장
      localStorageUtil.set(localStorageKey, {
        ...totalArtistThumbnailList,
        [artistName]: [
          ...(totalArtistThumbnailList[artistName] || []),
          thumbnailUrl,
        ],
      });

      return thumbnailUrl; // 업로드된 썸네일 URL 반환
    } catch (error) {
      console.error("Error uploading artist thumbnail:", error);
      throw new Error("Thumbnail upload failed");
    }
  };

  return {
    totalArtistThumbnailList,
    fetchAllArtistThumbnailList,
    removeCachedArtistThumbnailList,
    uploadArtistThumbnail,
    refetchArtistThumbnailList,
  };
};
